import { useEffect, useState } from "react";
import DashboardShell from "@/components/DashboardShell";
import PrintLogoHeader from "@/components/PrintLogoHeader";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Printer, Mail } from "lucide-react";
import DemoQRCode from "@/components/DemoQRCode";

export default function DeclineDriveLetterPage() {
  const [s, setS] = useState(null);
  useEffect(() => {
    api.get("/settings").then((r) => setS(r.data)).catch(() => {});
  }, []);
  const dealer = s?.dealership_name || "________________________";
  const today = new Date().toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
  const owner = s?.owner_name || "James Ayers";
  const ownerEmail = s?.owner_email || "jayers.me@gmail.com";
  const ownerPhone = s?.owner_phone || "(913) 408-7100";

  const mailto = `mailto:?subject=${encodeURIComponent(
    "Decline Drive — A Personal Introduction"
  )}&body=${encodeURIComponent(
    `Please see attached my personal letter of introduction.\n\n${owner}\nDecline Drive™ by Ayers Enterprises\n${ownerEmail} · ${ownerPhone}`
  )}`;

  return (
    <DashboardShell>
      <PrintLogoHeader docTitle="Decline Drive Letter" />
      <div className="mb-6 flex items-start justify-between gap-4 no-print">
        <div>
          <div className="font-mono-label text-muted-foreground">Sales Collateral</div>
          <h1 className="font-display text-4xl font-bold tracking-tighter mt-1">
            Decline Drive Letter
          </h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-xl">
            A personal cover letter from the founder to the dealer principal. Print it, sign it,
            hand it over with the One-Pager. Old-school, but it lands.
          </p>
        </div>
        <div className="flex gap-2">
          <a href={mailto} data-testid="dd-letter-email-link">
            <Button variant="outline">
              <Mail className="w-4 h-4 mr-2" strokeWidth={1.75} />
              Email letter
            </Button>
          </a>
          <Button onClick={() => window.print()} data-testid="print-dd-letter-btn">
            <Printer className="w-4 h-4 mr-2" strokeWidth={1.75} />
            Print letter
          </Button>
        </div>
      </div>

      <div
        id="dd-letter"
        className="bg-white text-neutral-900 max-w-[8.5in] mx-auto shadow-2xl border border-border rounded-lg overflow-hidden"
        data-testid="dd-letter-sheet"
      >
        <div className="bg-navy text-white px-10 py-8 flex items-center gap-6">
          <img
            src="/logo-brand.png"
            alt="Decline Drive"
            className="w-40 h-40 sm:w-44 sm:h-44 object-contain shrink-0"
          />
          <div>
            <div className="font-display font-black text-3xl sm:text-4xl tracking-tight">
              Decline Drive™
            </div>
            <div className="text-base sm:text-lg text-[hsl(var(--gold-light))] italic mt-1">
              We Drive Further
            </div>
            <div className="text-xs text-white/70 mt-2">
              Ayers Enterprises
            </div>
          </div>
        </div>
        <div className="gold-divider" />

        <div className="px-10 py-10 leading-relaxed text-[0.95rem]">
          <div className="text-right text-neutral-500 mb-8">{today}</div>

          <div className="mb-8">
            <div className="font-semibold">Dealer Principal</div>
            <div>{dealer}</div>
          </div>

          <div className="mb-6">Dear Dealer Principal,</div>

          <p className="mb-5">
            Right now, a customer is standing at your service counter saying &quot;not today.&quot;
          </p>

          <p className="mb-5">
            I&apos;m <b>{owner}</b>. I&apos;ve spent 42 years in fixed operations, 25 of them with
            Hendrick Automotive Group. At every store I&apos;ve run, I&apos;ve watched the same leak:
            declined service walking out the drive and never coming back.
          </p>

          <p className="mb-5">
            Pull last month&apos;s declined-service report and add it up. That money is already
            in your building. The customer trusted you with the car, your tech found the work,
            your advisor quoted it. Nobody follows up, because your advisors are pinned to the
            drive, not a follow-up log.
          </p>

          <p className="mb-5">
            <b>Decline Drive</b> follows up for you. Every night it pulls the day&apos;s declines
            from your DMS. Each customer gets a short series of texts, each with a unique coupon
            tied to their RO and a one-tap link to schedule, on the schedule and discounts <i>you</i>{" "}
            control. Your advisors don&apos;t lift a finger. And you see every recovered dollar by
            advisor, by op code and by RO.
          </p>

          <p className="mb-5">
            I&apos;m not a software salesman. I&apos;m a fixed ops guy who built the tool I wish
            I&apos;d had. It&apos;s backed by a <b>60-day guarantee</b>.
          </p>

          <p className="mb-6">
            Give me <b>15 minutes</b>. If I haven&apos;t shown you real money in the first five,
            I&apos;ll shake your hand, leave the coffee, and thank you for your time.
          </p>

          <p className="mb-8">Respectfully,</p>

          <div className="mb-2 font-display italic text-xl">{owner}</div>
          <div className="text-sm text-neutral-700">Founder, Decline Drive™ · Ayers Enterprises</div>
          <div className="mt-2 text-sm text-neutral-600">
            <span className="mr-4">{ownerPhone}</span>
            <span>{ownerEmail}</span>
          </div>

          <div className="mt-10 border-t border-neutral-300 pt-6 flex items-start gap-6">
            <div className="shrink-0">
              <DemoQRCode size={110} />
            </div>
            <div className="flex-1 text-sm">
              <div className="font-display font-bold text-base text-navy">
                Scan this — see it work in 30 seconds.
              </div>
              <div className="text-neutral-600 mt-1">
                No login. No form. Just the live customer journey from decline → text → booked RO.
                Watch it on your own phone right now.
              </div>
            </div>
          </div>
        </div>

        <div className="bg-neutral-100 border-t border-neutral-200 px-10 py-3 text-[0.65rem] text-neutral-500 text-center">
          © {new Date().getFullYear()} Decline Drive™ by Ayers Enterprises. All rights reserved.
        </div>
      </div>
    </DashboardShell>
  );
}
